import {
  alertError,
  alertSuccess,
  capitalize,
  getKeys,
  GiftItemStatus,
  GiftItemType,
  IGiftItemResponse,
  IOrganisationResponse,
  IPromotionResponse,
  itemWord,
  StateAction,
} from '@one-tree/library'
import isEqual from 'react-fast-compare'
import { IGiftItemFilters, IGiftItemPostBody } from '../types/GiftItemTypes'
import { deleteGiftItem, patchGiftItem } from './APIItemHelper'
import { isGiftItemValid } from './GiftItemValidityHelper'

import { ICategoryResponse } from '../types/API'

export const filterGiftItems = (
  input: IGiftItemResponse[],
  filters: IGiftItemFilters,
  organisation: IOrganisationResponse | null,
  promotions?: IPromotionResponse[],
  categories?: ICategoryResponse[],
): IGiftItemResponse[] => {
  const { Archived } = GiftItemStatus

  const selectedPromo = filters.promotion
    ? promotions?.find(({ id }) => id === filters.promotion)
    : undefined

  return input.filter((item: IGiftItemResponse) => {
    const withStatus = item.status === filters.status
    const withType = item.type === filters.type
    const withCategory = item.categoryIds
      && filters.category
      && item.categoryIds.includes(filters.category)

    if (filters.unfinished) {
      const isUnfinished = !isGiftItemValid(item, organisation, 'unfinished', categories)
        && item.status !== Archived

      if (!isUnfinished) return false
    }

    if (filters.expired) {
      const isExpired = !isGiftItemValid(item, organisation, 'expired', categories)
        && item.status !== Archived

      if (!isExpired) return false
    }

    if (filters.promotion) {
      const hasPromotion = item.id && (selectedPromo?.giftItems.includes(item.id) ?? false)
      if (!hasPromotion) return false
    }

    // don't show archived by default
    const toggleArchived = (filters.status !== Archived && item.status !== Archived)
      || (filters.status === Archived && item.status === Archived)

    // status and unfinished can't be set at the same time
    if (filters.status && filters.type && filters.category) {
      return withStatus && withType && withCategory && toggleArchived
    }
    if (filters.unfinished && filters.type && filters.category) {
      return withType && withCategory && toggleArchived
    }

    if (filters.status && filters.type) {
      return withStatus && withType && toggleArchived
    }
    if (filters.status && filters.category) {
      return withStatus && withCategory && toggleArchived
    }

    if (filters.unfinished && filters.type) {
      return withType && toggleArchived
    }
    if (filters.unfinished && filters.category) {
      return withCategory && toggleArchived
    }

    if (filters.type && filters.category) {
      return withType && withCategory && toggleArchived
    }

    // if we are filtering on status only, toggleArchived is redundant
    if (filters.status) {
      return withStatus
    }
    if (filters.type) {
      return withType && toggleArchived
    }
    if (filters.category) {
      return withCategory && toggleArchived
    }

    return input && toggleArchived
  })
}

export const typeHandler = (data: IGiftItemResponse[] | false): string[] => {
  const typeSet = new Set<string>()
  if (data) {
    for (let i = 0; i < data.length; i += 1) {
      const { type } = data[i]
      if (type) typeSet.add(type)
    }
  }
  const typeArray = Array.from(typeSet)
  return typeArray.sort()
}

export const publishSwitchHandler = (
  publish: boolean,
  changeItem: (changes: IGiftItemPostBody) => void,
  setForcePublish: (how: boolean) => void,
): void => {
  changeItem({
    status: publish ? GiftItemStatus.Published : GiftItemStatus.Unpublished,
  })
  setForcePublish(publish)
}

export const getItemStatusText = (
  item: IGiftItemResponse,
  organisation: IOrganisationResponse | null,
  categories?: ICategoryResponse[],
): string => {
  const isFinished = isGiftItemValid(item, organisation, 'unfinished', categories)
  const isExpired = isGiftItemValid(item, organisation, 'expired', categories)
  const isComplimentary = item.type === GiftItemType.Complimentary

  const hasPrices = item.prices && item.prices.length > 0

  const hasPricesButNoPublishedPrices = item.prices
    && item.prices.length > 0
    && item.prices.every((price) => price.status !== GiftItemStatus.Published)

  const noPricesNoValue = !hasPrices && !item.value

  if (!isFinished) return 'Unfinished'
  if (!isExpired) return 'Expired'
  if (isComplimentary) return 'Complimentary'
  if (noPricesNoValue) return 'Requires value'
  if (hasPricesButNoPublishedPrices) return 'Requires price'

  return capitalize(item.status)
}

export enum View {
  Full = 'Full',
  Compact = 'Compact',
}
export const getView = (view: string | null): View => {
  if (view === View.Full || view === View.Compact) return view
  return View.Full
}

export const handleArchiveItem = async (
  organisation: IOrganisationResponse | null,
  setData: StateAction<IGiftItemResponse[]>,
  id?: number,
): Promise<void> => {
  if (!organisation || !id) return
  const wording = itemWord(organisation.format)

  const res = await deleteGiftItem({ format: organisation.format, id })

  if (res) {
    alertSuccess(`${capitalize(wording)} archived`)
    setData((p) => p.filter((item) => item.id !== id))
  } else {
    alertError(`Error archiving ${wording}`)
  }
}

export const handleRestoreItem = async (
  organisation: IOrganisationResponse | null,
  setData?: StateAction<IGiftItemResponse[]>,
  id?: number,
): Promise<void> => {
  if (!organisation || !id) return
  const wording = itemWord(organisation.format)

  const res = await patchGiftItem({
    format: organisation.format,
    itemId: id,
    patchOptions: { status: GiftItemStatus.Unpublished },
  })

  if (res) {
    alertSuccess(`${capitalize(wording)} restored`)
    if (setData) setData((p) => p.filter((item) => item.id !== id))
  } else {
    alertError(`Error restoring ${wording}`)
  }
}

// Deep comparison - reverting an array or object change clears it
export function getActualChanges<T extends object>(
  base?: T,
  changes?: T,
): T | null {
  if (!base || !changes) return changes || null
  const cleaned = { ...changes }
  getKeys(changes).forEach((key) => {
    if (isEqual(base[key], changes[key])) delete cleaned[key]
  })
  return getKeys(cleaned).length ? cleaned : null
}
